import { useSuspenseQuery } from "@apollo/client";
import { type ReactNode } from "react";
import { supplyChainBookingFieldSettingsQuery } from "../BookingForm";
import { formStructure } from "../BookingForm/constants";
import { type MetadataField, type SectionId } from "../BookingForm/types";
import { type MetadataBooking } from ".";
import { Badge } from "@/components/ui/badge";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { toDisplayDate } from "@/lib/utils/dates";
import { formatEmptyableValue, formatEnumLabel, formatMetricValue } from "@/lib/utils/formatting";

interface MetadataDisplayItemDefinition {
  label: string;
  getValue: (booking: MetadataBooking | undefined) => ReactNode;
  fullWidth: boolean;
  section: SectionId;
}

/**
 * A mapping of metadata fields to their display properties
 */
const metadataDisplayMap: Record<MetadataField, MetadataDisplayItemDefinition> = {
  // Lot & Inventory Details
  LOT_NUMBER: {
    label: "Lot Number",
    getValue: (b) => formatEmptyableValue(b?.lotNumber),
    fullWidth: false,
    section: "lot_inventory",
  },
  PEN_NUMBER: {
    label: "Pen Number",
    getValue: (b) => formatEmptyableValue(b?.penNumber),
    fullWidth: false,
    section: "lot_inventory",
  },
  ACCOUNT_NUMBER: {
    label: "Contract Number",
    getValue: (b) => formatEmptyableValue(b?.accountNumber),
    fullWidth: false,
    section: "lot_inventory",
  },
  SEX_CLASS: {
    label: "Sex Class",
    getValue: (b) => (b?.sexClass ? formatEnumLabel(b?.sexClass) : "\u2014"),
    fullWidth: false,
    section: "lot_inventory",
  },
  BREED_NOTE: {
    label: "Breed Note",
    getValue: (b) => formatEmptyableValue(b?.breedNote),
    fullWidth: true,
    section: "lot_inventory",
  },
  ACCREDITATIONS: {
    label: "Accreditations",
    getValue: (b) => {
      if (!b?.accreditations || b.accreditations.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {b.accreditations.map((acc) => (
            <Badge
              key={acc.id}
              variant="secondary"
              className="gap-2 h-8 px-4 font-medium border-none rounded-full bg-blue-500 text-white"
            >
              {acc.label}
            </Badge>
          ))}
        </div>
      );
    },
    fullWidth: false,
    section: "lot_inventory",
  },
  CONFIRMED: {
    label: "Status",
    getValue: (b) => (b?.confirmed === true ? "Confirmed" : b?.confirmed === false ? "Unconfirmed" : "\u2014"),
    fullWidth: false,
    section: "lot_inventory",
  },
  LABELS: {
    label: "Labels",
    getValue: (b) => {
      if (!b?.labels || b.labels.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {b.labels.map((label) => (
            <Badge
              key={label.id}
              variant="secondary"
              className="gap-2 h-8 px-4 font-medium border-none rounded-full bg-green-500 text-white"
            >
              {label.label}
            </Badge>
          ))}
        </div>
      );
    },
    fullWidth: false,
    section: "lot_inventory",
  },
  CONDITION: {
    label: "Condition",
    getValue: (b) => (b?.condition ? formatEnumLabel(b?.condition) : "\u2014"),
    fullWidth: false,
    section: "weight",
  },
  CONDITIONS: {
    label: "Conditions",
    getValue: (b) => {
      if (!b?.conditions || b.conditions.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {b.conditions.map((condition) => {
            return (
              <Badge
                key={condition}
                variant="secondary"
                className="gap-2 h-8 px-4 font-medium border-none rounded-full bg-slate-500 text-white"
              >
                {condition}
              </Badge>
            );
          })}
        </div>
      );
    },
    fullWidth: false,
    section: "weight",
  },
  OWNERSHIP_TYPE: {
    label: "Ownership Type",
    getValue: (b) => (b?.ownershipType ? formatEnumLabel(b?.ownershipType) : "\u2014"),
    fullWidth: false,
    section: "lot_inventory",
  },

  // Dates & Scheduling
  DATE_OF_SALE: {
    label: "Date of Sale",
    getValue: (b) => (b?.dateOfSale ? toDisplayDate(b.dateOfSale) : "\u2014"),
    fullWidth: false,
    section: "dates",
  },
  DATE_OF_VISIT: {
    label: "Date of Visit",
    getValue: (b) => (b?.dateOfVisit ? toDisplayDate(b.dateOfVisit) : "\u2014"),
    fullWidth: false,
    section: "dates",
  },
  PROJECTED_OUT_DATE: {
    label: "Projected Out Date",
    getValue: (b) => (b?.projectedOutDate ? toDisplayDate(b.projectedOutDate) : "\u2014"),
    fullWidth: false,
    section: "dates",
  },
  DATE_DELIVERED: {
    label: "Date Delivered",
    getValue: (b) => (b?.dateDelivered ? toDisplayDate(b.dateDelivered) : "\u2014"),
    fullWidth: false,
    section: "dates",
  },

  // Financial Details
  PRICE: {
    label: "Price",
    getValue: (b) => formatMetricValue({ value: b?.price, fallbackLabel: "\u2014", metricType: "currency" }),
    fullWidth: false,
    section: "financial",
  },
  FREIGHT_COST: {
    label: "Freight Cost",
    getValue: (b) => formatMetricValue({ value: b?.freightCost, fallbackLabel: "\u2014", metricType: "currency" }),
    fullWidth: false,
    section: "financial",
  },
  SLIDE: {
    label: "Slide",
    getValue: (b) =>
      formatMetricValue({ value: String(b?.slide ?? ""), fallbackLabel: "\u2014", metricType: "weight" }),
    fullWidth: false,
    section: "financial",
  },
  PURCHASE_PRICE_PER_CWT: {
    label: "Purchase Price/Cwt",
    getValue: (b) =>
      formatMetricValue({
        value: b?.purchasePricePerCwt,
        fallbackLabel: "\u2014",
        metricType: "currency",
      }),
    fullWidth: false,
    section: "financial",
  },
  RISK_MANAGEMENT: {
    label: "Risk Management",
    getValue: (b) => (b?.riskManagement === true ? "Yes" : b?.riskManagement === false ? "No" : "\u2014"),
    fullWidth: false,
    section: "financial",
  },
  // Weight & Quality Information
  SHIPPED_WEIGHT: {
    label: "Shipped Weight",
    getValue: (b) =>
      formatMetricValue({
        value: String(b?.shippedWeight ?? ""),
        fallbackLabel: "\u2014",
        metricType: "weight",
      }),
    fullWidth: false,
    section: "weight",
  },
  BASE_WEIGHT: {
    label: "Base Weight",
    getValue: (b) =>
      formatMetricValue({
        value: String(b?.baseWeight ?? ""),
        fallbackLabel: "\u2014",
        metricType: "weight",
      }),
    fullWidth: false,
    section: "weight",
  },
  ESTIMATED_CHOICE_PERCENT: {
    label: "Choice %",
    getValue: (b) =>
      formatMetricValue({
        value: String(b?.estimatedChoicePercent ? b.estimatedChoicePercent / 100 : ""),
        fallbackLabel: "\u2014",
        metricType: "percentage",
      }),
    fullWidth: false,
    section: "weight",
  },
  SHRINK_PERCENT: {
    label: "Shrink %",
    getValue: (b) =>
      formatMetricValue({
        value: String(b?.shrinkPercent ? b.shrinkPercent / 100 : ""),
        fallbackLabel: "\u2014",
        metricType: "percentage",
      }),
    fullWidth: false,
    section: "weight",
  },
  ESTIMATED_WEIGHT_NOTE: {
    label: "Est. Weight Note",
    getValue: (b) =>
      b?.estimatedWeightNote ? <div className="whitespace-pre-wrap break-words">{b.estimatedWeightNote}</div> : "—",
    fullWidth: true,
    section: "weight",
  },

  // Source & Documentation
  SOURCE_NOTE: {
    label: "Source Note",
    getValue: (b) => formatEmptyableValue(b?.sourceNote),
    fullWidth: true,
    section: "source",
  },
  BRAND_NOTE: {
    label: "Brand Note",
    getValue: (b) => formatEmptyableValue(b?.brandNote),
    fullWidth: true,
    section: "source",
  },
  CERTIFICATE_NUMBER: {
    label: "Certificate Number",
    getValue: (b) => formatEmptyableValue(b?.certificateNumber),
    fullWidth: false,
    section: "source",
  },
  PROGRAM: {
    label: "Program",
    getValue: (b) => formatEmptyableValue(b?.program?.label),
    fullWidth: false,
    section: "source",
  },
  GRID: {
    label: "Grid",
    getValue: (b) => formatEmptyableValue(b?.grid?.label),
    fullWidth: false,
    section: "source",
  },

  // Transportation & Services
  TRUCKING_COMPANY: {
    label: "Trucking Company",
    getValue: (b) => formatEmptyableValue(b?.truckingCompany?.businessName),
    fullWidth: false,
    section: "transportation",
  },
  BROKER: {
    label: "Broker",
    getValue: (b) => formatEmptyableValue(b?.broker?.businessName),
    fullWidth: false,
    section: "transportation",
  },
  VETERINARIAN: {
    label: "Veterinarian",
    getValue: (b) =>
      b?.veterinarian?.user ? `${b.veterinarian.user.firstName} ${b.veterinarian.user.lastName}` : "\u2014",
    fullWidth: false,
    section: "transportation",
  },
  SERVICE_PROVIDED: {
    label: "Service Provided",
    getValue: (b) =>
      b?.serviceProvided ? <div className="whitespace-pre-wrap break-words">{b.serviceProvided}</div> : "—",
    fullWidth: true,
    section: "transportation",
  },
};

/**
 * Hook to handle displaying metadata with field visibility settings
 */
export function useBookingMetadataDisplay(booking: Maybe<MetadataBooking>) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { data } = useSuspenseQuery(supplyChainBookingFieldSettingsQuery, {
    variables: { id: activeBusinessUnitId || "" },
  });

  const fieldSettings = removeNothings(data.supplyChainBookingFieldSettings || []);

  const visibilityMap = new Map(fieldSettings.map((setting) => [setting.field, setting.isVisible]));

  // Group fields by section and maintain the section order from formStructure
  const sections = formStructure
    .map((section) => {
      // Get all fields for this section that are visible
      const sectionFields = section.fields
        .filter((field) => visibilityMap.get(field.id) !== false)
        .map((field) => {
          const info = metadataDisplayMap[field.id];
          const value = booking ? info.getValue(booking) : "\u2014";

          return {
            id: field.id,
            label: info.label,
            value,
            // Only use fullWidth if it's supposed to be fullWidth AND the value is not the empty placeholder
            fullWidth: info.fullWidth && value !== "\u2014",
            section: info.section,
          };
        });

      return {
        id: section.id,
        title: section.title,
        items: sectionFields,
      };
    })
    .filter((section) => section.items.length > 0); // Remove sections with no visible fields

  // Calculate total visible fields across all sections
  const totalVisibleFields = sections.reduce((count, section) => count + section.items.length, 0);

  return {
    sections,
    hasVisibleFields: totalVisibleFields > 0,
  } as const;
}
